import { AiOutlineArrowUp, AiOutlineArrowDown } from "react-icons/ai";
import { RiLockPasswordLine, RiUserForbidLine, RiUserAddLine } from "react-icons/ri";
import React, { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
} from "@tanstack/react-table";
import { UserData } from "@/types/user";
import styles from "./css/UsersTable.module.css";
import { useSelector, useDispatch } from "react-redux";
import { truncateText } from "@/utils/truncateText";
import { UserAvatar } from "@/components/addons/UserAvatar";
import { RootState } from "@/store/store";
import { useToggleUserStatus } from "@/hooks/user/useToggleUserStatus";
import { setToast } from "@/store/systemSlice";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/Buttons";
import GenericConfirmModal from "@/components/modals/GenericModal";
import PasswordResetModal from "./PasswordResetModal";

interface UserManagementTableProps {
  className?: string;
  data: UserData[];
  onDataChange: () => void;
  onRowClick?: (user: UserData) => void;
}

const UserManagementTable: React.FC<UserManagementTableProps> = ({ 
  className, 
  data, 
  onDataChange,
  onRowClick 
}) => {
  const { t } = useTranslation('UsersManagement');
  const { t: tRoles } = useTranslation('RolesNames');
  const [localData, setLocalData] = useState<UserData[]>(data);
  const [updatingUserId, setUpdatingUserId] = useState<string>("");
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    userId: string;
    userName: string;
    currentStatus: boolean;
  }>({
    isOpen: false,
    userId: "",
    userName: "",
    currentStatus: true,
  });
  const [passwordModal, setPasswordModal] = useState<{
    isOpen: boolean;
    userId: string;
    userName: string;
  }>({
    isOpen: false,
    userId: "",
    userName: "",
  });
  
  const user = useSelector((state: RootState) => state.user.user);
  const dispatch = useDispatch();

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const toggleUserStatusMutation = useToggleUserStatus({
    onSuccess: (data) => {
      onDataChange();
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: data.message,
          toastType: "success",
        })
      );
      setUpdatingUserId("");
      setConfirmModal({ isOpen: false, userId: "", userName: "", currentStatus: true });
    },
    onError: (error: any) => {
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: error.response?.data?.message || "שגיאה בעדכון סטטוס המשתמש",
          toastType: "error",
        })
      );
      setUpdatingUserId("");
      setConfirmModal({ isOpen: false, userId: "", userName: "", currentStatus: true });
    },
  });

  const handleStatusToggle = (userId: string, userName: string, currentStatus: boolean) => {
    setConfirmModal({
      isOpen: true,
      userId,
      userName,
      currentStatus,
    });
  };

  const handlePasswordReset = (userId: string, userName: string) => {
    setPasswordModal({
      isOpen: true,
      userId,
      userName,
    });
  };

  const confirmStatusToggle = () => {
    if (toggleUserStatusMutation.isPending) return;
    
    setUpdatingUserId(confirmModal.userId);
    toggleUserStatusMutation.mutate({
      user_id: confirmModal.userId,
      active: !confirmModal.currentStatus,
    });
  };

  const columns = useMemo<ColumnDef<UserData>[]>(
    () => [
      {
        accessorKey: "color",
        header: "",
        cell: ({ row }) => {
          const color = row.original.color;
          const image = row.original.user_image;
          return <UserAvatar color={color || "blue"} userImage={image} />;
        },
      },
      {
        accessorKey: "user_name",
        header: t("userName"),
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return (
            <div className={styles.userNameColumn}>
              {truncateText(value, 26)}
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: t("email"),
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return (
            <div className={styles.emailColumn}>{truncateText(value, 30)}</div>
          );
        },
      },
      {
        accessorKey: "user_type",
        header: t("userType"),
        cell: ({ getValue, row }) => {
          const hasAO = row.original.ao_id;
          const roleName = row.original.role_name;
          
          // For AO users, display "משרד אדריכלים"
          if (hasAO) {
            return (
              <div className={styles.userTypeColumn}>
                {t("architectOffice")}
              </div>
            );
          }
          // For city users, display translated role name
          return (
            <div className={styles.userTypeColumn}>
              {roleName ? tRoles(roleName) : t("noRole")}
            </div>
          );
        },
      },
      {
        accessorKey: "city_or_ao",
        header: t("cityOrAO"),
        cell: ({ row }) => {
          const hasAO = row.original.ao_id;
          const cityName = row.original.city_name;
          const aoName = row.original.ao_name;
          const aoCityNames = row.original.ao_city_names || [];
          
          // For AO users, display AO name and cities they can access
          if (hasAO) {
            return (
              <div className={styles.aoColumn}>
                <div className={styles.aoName}>{aoName || t("noAO")}</div>
                {aoCityNames.length > 0 && (
                  <div className={styles.aoCities}>
                    {t("cities")}: {aoCityNames.join(", ")}
                  </div>
                )}
              </div>
            );
          }
          
          // For city users, display city name
          return (
            <div className={styles.cityColumn}>
              {cityName ? truncateText(cityName, 20) : t("noCity")}
            </div>
          );
        },
      },
      {
        accessorKey: "active",
        header: t("status"),
        cell: ({ getValue, row }) => {
          const isActive = getValue<boolean>();
          const isCurrentUser = row.original.id === user.id;
          
          return (
            <div className={styles.statusColumn}>
              <span className={`${styles.statusBadge} ${isActive ? styles.active : styles.inactive}`}>
                {isActive ? t("active") : t("inactive")}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: t("actions"),
        cell: ({ row }) => {
          const isCurrentUser = row.original.id === user.id;
          const isActive = row.original.active;
          const isPending = updatingUserId === row.original.id && toggleUserStatusMutation.isPending;
          
          return (
            <div className={styles.actionsColumn} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2">
                <button
                  id={`toggle-${row.original.id}`}
                  className={`p-2 rounded-md transition-colors ${
                    isActive 
                      ? 'text-red-600 hover:bg-red-50' 
                      : 'text-blue-600 hover:bg-blue-50'
                  } ${isCurrentUser || isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  disabled={isCurrentUser || isPending}
                  onClick={() => handleStatusToggle(
                    row.original.id, 
                    row.original.user_name, 
                    isActive ?? true
                  )}
                  title={isActive ? t("deactivate") : t("activate")}
                >
                  {isPending ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : isActive ? (
                    <RiUserForbidLine className="w-5 h-5" />
                  ) : (
                    <RiUserAddLine className="w-5 h-5" />
                  )}
                </button>
                <button
                  id={`reset-password-${row.original.id}`}
                  className={`p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors ${
                    isCurrentUser || isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                  disabled={isCurrentUser || isPending}
                  onClick={() => handlePasswordReset(
                    row.original.id, 
                    row.original.user_name
                  )}
                  title={t("resetPassword")}
                >
                  <RiLockPasswordLine className="w-5 h-5" />
                </button>
              </div>
            </div>
          );
        },
      },
    ],
    [toggleUserStatusMutation, updatingUserId, user.id, t]
  );

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: localData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className={`${styles.tableContainer} ${className}`}>
        <table className={styles.table}>
          <thead className={styles.tableHeadersRow}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={`${styles.headerText} ${
                      styles[header.id + "Column"]
                    }`}
                    onClick={
                      header.id !== "color" && header.id !== "actions"
                        ? header.column.getToggleSortingHandler()
                        : () => {}
                    }
                  >
                    <div className="flex items-center">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.id !== "color" && header.id !== "actions" && (
                        <span className="ml-2">
                          {{
                            asc: <AiOutlineArrowUp className="text-black" />,
                            desc: <AiOutlineArrowDown className="text-black" />,
                            undefined: (
                              <AiOutlineArrowUp className="text-black" />
                            ),
                          }[header.column.getIsSorted() as string] ?? (
                            <AiOutlineArrowUp className="text-black" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={styles.bodyRow}>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={styles.rowHover}
                onClick={() => onRowClick && onRowClick(row.original)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`${styles.rowText}`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Status Toggle Confirmation Modal */}
      <GenericConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.currentStatus ? t("deactivateUserTitle") : t("activateUserTitle")}
        description={confirmModal.currentStatus 
          ? t("deactivateUserDescription", { userName: confirmModal.userName })
          : t("activateUserDescription", { userName: confirmModal.userName })
        }
        confirmText={confirmModal.currentStatus ? t("deactivate") : t("activate")}
        cancelText={t("cancel")}
        onConfirm={confirmStatusToggle}
        onCancel={() => setConfirmModal({ isOpen: false, userId: "", userName: "", currentStatus: true })}
        loading={toggleUserStatusMutation.isPending}
        type="escape"
      />

      {/* Password Reset Modal */}
      <PasswordResetModal
        isOpen={passwordModal.isOpen}
        userId={passwordModal.userId}
        userName={passwordModal.userName}
        onClose={() => setPasswordModal({ isOpen: false, userId: "", userName: "" })}
        onSuccess={onDataChange}
      />
    </>
  );
};

export default React.memo(UserManagementTable, (prevProps, nextProps) => {
  return (
    JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
    prevProps.className === nextProps.className
  );
});